import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Snackbar,
  Alert,
  Fade,
} from '@mui/material';
import { useDashboardData } from './useDashboardData';
import WelcomePage from './WelcomePage';
import HeroSection from './HeroSection';
import TransformationPipeline from './TransformationPipeline';
import QuickStats from './QuickStats';
import GettingStartedGuide from './GettingStartedGuide';
import SmartUploadDialog from './SmartUploadDialog';
import type { UploadCompletionResult } from './SmartUploadDialog';
import RecipePickerDialog from './RecipePickerDialog';
import CompatibilityDialog from './CompatibilityDialog';
import { apiService } from '../../services/api';
import * as mappingApi from '../../services/mappingApi';
import type { CompatibilityVerdict, ProjectRecipeSummary } from '../../services/types';

const blinkAnimation = {
  animation: 'pageFlash 350ms ease-in-out',
  '@keyframes pageFlash': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.3 },
    '100%': { opacity: 1 },
  },
};

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const data = useDashboardData();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [blinkKey, setBlinkKey] = useState(0);

  // Dialog state
  const [uploadOpen, setUploadOpen] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);

  // Cross-project bootstrap state
  const [recipePickerOpen, setRecipePickerOpen] = useState(false);
  const [bootstrapBatchId, setBootstrapBatchId] = useState<string | null>(null);
  const [bootstrapRecipe, setBootstrapRecipe] = useState<ProjectRecipeSummary | null>(null);
  const [bootstrapVerdict, setBootstrapVerdict] = useState<CompatibilityVerdict | null>(null);
  const [bootstrapCompatOpen, setBootstrapCompatOpen] = useState(false);
  const [bootstrapBusy, setBootstrapBusy] = useState(false);

  // Listen for 'open-new-transformation' event (e.g. after project creation)
  useEffect(() => {
    const handler = () => setUploadOpen(true);
    window.addEventListener('open-new-transformation', handler);
    return () => window.removeEventListener('open-new-transformation', handler);
  }, []);

  // SmartUploadDialog completion — handles all post-upload + compatibility actions
  const handleUploadFlowComplete = useCallback(
    (result: UploadCompletionResult) => {
      setUploadOpen(false);
      data.refreshSummary();

      switch (result.action) {
        case 'apply_and_load': {
          const rows = result.executionResult?.rows_processed ?? 0;
          setToast({ severity: 'success', message: `Data loaded successfully — ${rows.toLocaleString()} rows.` });
          break;
        }
        case 'review_first':
          navigate(`/batches/${result.batchId}/workspace`);
          break;
        case 'regenerate_delta':
        case 'regenerate_full':
          setToast({ severity: 'success', message: 'Upload received. Redirecting to mapping canvas...' });
          navigate(`/batches/${result.batchId}/workspace`);
          break;
        case 'cancel':
          // User cancelled — batch was deleted, just stay on dashboard
          break;
        case 'none':
        default:
          setToast({ severity: 'success', message: 'Upload received. Your data is being analyzed.' });
          break;
      }
    },
    [data.refreshSummary, navigate],
  );

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    setBlinkKey((k) => k + 1);

    try {
      await data.refreshSummary();
    } finally {
      setTimeout(() => {
        setIsRefreshing(false);
      }, 450);
    }
  }, [isRefreshing, data.refreshSummary]);

  // Bootstrap from Project — opens RecipePickerDialog for a specific batch
  const handleBootstrapFromProject = useCallback((batchId: string) => {
    setBootstrapBatchId(batchId);
    setRecipePickerOpen(true);
  }, []);

  // When user selects an external recipe from RecipePickerDialog
  const handleSelectExternalRecipe = useCallback(async (selectedRecipe: ProjectRecipeSummary) => {
    if (!bootstrapBatchId) return;
    setRecipePickerOpen(false);
    setBootstrapRecipe(selectedRecipe);
    try {
      const compat = await mappingApi.checkSchemaCompatibility(
        selectedRecipe.recipe_id,
        bootstrapBatchId,
      );
      setBootstrapVerdict(compat);
      setBootstrapCompatOpen(true);
    } catch (error) {
      console.error('Cross-project compatibility check failed:', error);
      setToast({ severity: 'error', message: 'Failed to check compatibility with selected recipe.' });
      setBootstrapRecipe(null);
    }
  }, [bootstrapBatchId]);

  // Bootstrap: Apply & Load directly
  const handleBootstrapApply = useCallback(async () => {
    if (!bootstrapRecipe || !bootstrapBatchId || bootstrapBusy) return;
    setBootstrapBusy(true);
    try {
      const result = await mappingApi.applyRecipe(bootstrapRecipe.recipe_id, bootstrapBatchId);
      setBootstrapCompatOpen(false);
      setBootstrapBusy(false);
      if (result.action === 'direct_execution' && result.execution_result) {
        const rows = result.execution_result.rows_processed ?? 0;
        setToast({ severity: 'success', message: `Data loaded successfully — ${rows.toLocaleString()} rows.` });
      } else {
        setToast({ severity: 'success', message: 'Recipe applied. Check results.' });
      }
      data.refreshSummary();
    } catch (error) {
      console.error('Bootstrap Apply & Load failed:', error);
      setBootstrapBusy(false);
      setToast({ severity: 'error', message: 'Failed to apply recipe. Please try again.' });
    }
  }, [bootstrapRecipe, bootstrapBatchId, bootstrapBusy, data.refreshSummary]);

  // Bootstrap: Review in canvas first
  const handleBootstrapReview = useCallback(() => {
    if (!bootstrapBatchId) return;
    setBootstrapCompatOpen(false);
    navigate(`/batches/${bootstrapBatchId}/workspace`);
  }, [bootstrapBatchId, navigate]);

  // Bootstrap: Regenerate delta
  const handleBootstrapDelta = useCallback(async () => {
    if (!bootstrapBatchId || !data.selectedProjectId) return;
    setBootstrapCompatOpen(false);
    try {
      await apiService.generateMappings(bootstrapBatchId, {
        projectId: data.selectedProjectId,
        targetSchemaId: data.targetSchema?.schema_id ?? null,
        generationMode: 'auto',
        metadata: bootstrapRecipe ? { baseline_recipe_id: bootstrapRecipe.recipe_id } : undefined,
      });
      setToast({ severity: 'success', message: 'Delta regeneration started. Redirecting to mapping canvas...' });
      navigate(`/batches/${bootstrapBatchId}/workspace`);
    } catch {
      setToast({ severity: 'error', message: 'Failed to start delta regeneration.' });
    }
  }, [bootstrapBatchId, bootstrapRecipe, data.selectedProjectId, data.targetSchema, navigate]);

  // Bootstrap: Start fresh (full regen)
  const handleBootstrapFull = useCallback(async () => {
    if (!bootstrapBatchId || !data.selectedProjectId) return;
    setBootstrapCompatOpen(false);
    try {
      await apiService.generateMappings(bootstrapBatchId, {
        projectId: data.selectedProjectId,
        targetSchemaId: data.targetSchema?.schema_id ?? null,
        generationMode: 'full',
      });
      setToast({ severity: 'success', message: 'Full regeneration started. Redirecting to mapping canvas...' });
      navigate(`/batches/${bootstrapBatchId}/workspace`);
    } catch {
      setToast({ severity: 'error', message: 'Failed to start full regeneration.' });
    }
  }, [bootstrapBatchId, data.selectedProjectId, data.targetSchema, navigate]);

  // Bootstrap: Change baseline (re-open picker)
  const handleBootstrapChangeBaseline = useCallback(() => {
    setBootstrapCompatOpen(false);
    setRecipePickerOpen(true);
  }, []);

  // No project selected - show welcome
  if (!data.selectedProjectId) {
    return (
      <Box
        key={blinkKey}
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          ...(isRefreshing ? blinkAnimation : {}),
        }}
      >
        <WelcomePage onCreateProject={() => navigate('/projects')} hasProject={false} />
      </Box>
    );
  }

  const isEmptyProject = !data.isLoadingSummary && data.batches.length === 0;

  return (
    <Box
      key={blinkKey}
      sx={{
        minHeight: '100vh',
        width: '100%',
        bgcolor: 'background.default',
        ...(isRefreshing ? blinkAnimation : {}),
      }}
    >
      {/* Hero */}
      <Fade in timeout={300}>
        <Box>
          <HeroSection
            projectName={data.projectSummary?.project?.name}
            schemaSet={data.targetSchema}
            isLoading={data.isLoadingSummary}
            isRefreshing={isRefreshing}
            onUploadClick={() => setUploadOpen(true)}
            onRefresh={handleRefresh}
          />
        </Box>
      </Fade>

      {isEmptyProject ? (
        /* Empty project: show onboarding / upload prompt */
        <WelcomePage onCreateProject={() => setUploadOpen(true)} hasProject />
      ) : (
        /* Active project: show stats, pipeline, guide */
        <>
          {/* Quick Stats */}
          <Fade in timeout={400}>
            <Box>
              <QuickStats
                transformationsThisWeek={data.transformationsThisWeek}
                readyForReview={data.readyForReview}
                completedCount={data.completedCount}
                successRate={data.successRate}
                isLoading={data.isLoadingSummary}
              />
            </Box>
          </Fade>

          {/* Getting Started Guide */}
          <GettingStartedGuide
            totalBatches={data.batches.length}
            completedBatches={data.completedCount}
            readyForReview={data.readyForReview}
            onStartTransformation={() => setUploadOpen(true)}
          />

          {/* Transformation Pipeline */}
          <Fade in timeout={500}>
            <Box>
              <TransformationPipeline
                batches={data.batches}
                needsAttentionBatches={data.needsAttentionBatches}
                inProgressBatches={data.inProgressBatches}
                completedBatches={data.completedBatches}
                onBootstrapFromProject={handleBootstrapFromProject}
              />
            </Box>
          </Fade>
        </>
      )}

      {/* Smart Upload Dialog — handles upload + recipe compatibility check + Apply & Load */}
      <SmartUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onComplete={handleUploadFlowComplete}
        projectId={data.selectedProjectId}
        schemaSet={data.targetSchema}
        defaultSourceConnectorId={data.projectSummary?.project?.source_connector_id}
      />

      {/* Cross-project Bootstrap: RecipePickerDialog */}
      {data.selectedProjectId && (
        <RecipePickerDialog
          open={recipePickerOpen}
          onClose={() => setRecipePickerOpen(false)}
          onSelect={(selected) => handleSelectExternalRecipe(selected)}
          currentProjectId={data.selectedProjectId}
          currentTargetSchemaId={data.targetSchema?.schema_id}
        />
      )}

      {/* Cross-project Bootstrap: CompatibilityDialog */}
      {bootstrapCompatOpen && bootstrapBatchId && (
        <CompatibilityDialog
          open
          verdict={bootstrapVerdict}
          recipe={bootstrapRecipe}
          batchId={bootstrapBatchId}
          onApplyAndLoad={handleBootstrapApply}
          onReviewFirst={handleBootstrapReview}
          onRegenerateDelta={handleBootstrapDelta}
          onRegenerateFull={handleBootstrapFull}
          onCancel={() => setBootstrapCompatOpen(false)}
          busy={bootstrapBusy}
          baselineSource="cross_project"
          sourceProjectName={bootstrapRecipe?.project_name}
          onChangeBaseline={handleBootstrapChangeBaseline}
        />
      )}

      {/* Toast */}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert onClose={() => setToast(null)} severity={toast.severity} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default DashboardPage;
